import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { routes } from './app.routes';
import { CognitoService } from './services/cognito.service';
import { environment } from '../environments/environment';

const RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

const isApiRequest = (url: string): boolean => {
  const apiBaseUrl = (environment.apiBaseUrl ?? '').trim();

  // Toujours considérer les routes API relatives comme internes.
  if (url.startsWith('/api/')) {
    return true;
  }

  // N'utiliser apiBaseUrl que s'il est réellement défini.
  if (!apiBaseUrl) {
    return false;
  }

  return url.startsWith(apiBaseUrl);
};

/** Ajoute withCredentials sur toutes les requêtes (nécessaire pour les cookies HttpOnly). */
const credentialsInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }
  return next(req.clone({ withCredentials: true }));
};

/** Ajoute le header CSRF sur les requêtes mutatives envoyées à l'API. */
const csrfInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method.toUpperCase());
  if (!isMutating) {
    return next(req);
  }

  const csrfToken =
    typeof document !== 'undefined'
      ? document.cookie
          .split('; ')
          .find(row => row.startsWith('csrf_token='))
          ?.split('=')[1]
      : undefined;

  if (!csrfToken) {
    return next(req);
  }

  return next(req.clone({
    setHeaders: {
      'X-CSRF-Token': decodeURIComponent(csrfToken),
    },
  }));
};

const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url) || req.context.get(RETRY_AFTER_REFRESH)) {
    return next(req);
  }

  const cognitoService = inject(CognitoService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401 || req.url.includes('/api/auth/login')) {
        return throwError(() => err);
      }

      return from(cognitoService.refreshSession()).pipe(
        switchMap(() => next(req.clone({ context: req.context.set(RETRY_AFTER_REFRESH, true) }))),
        catchError(refreshErr => {
          cognitoService.handleSessionExpired();
          void router.navigate(['/login']);
          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(() => {
      inject(CognitoService).initializeSession();
    }),
    provideHttpClient(withInterceptors([credentialsInterceptor, csrfInterceptor, authRefreshInterceptor])),
    provideRouter(routes),
  ],
};
